/* MOD-CUSTOM: Quantity option buttons -> render from product metafield quantity_option_group + sync into Flow product-form quantity */

(() => {
  const CONTAINER_SEL = "[data-custom-quantity-options]";
  const GRID_SEL = "[data-custom-quantity-options-grid]";
  const DATA_SEL = "[data-custom-quantity-options-data]";
  const BTN_SEL = "[data-custom-quantity-option][data-quantity]";
  const INIT_ATTR = "data-custom-quantity-options-initialized";

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents);
    }
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    return (cents / 100).toLocaleString(undefined, { style: "currency", currency });
  }

  function formatMoneyRounded(cents) {
    return formatMoney(cents).replace(/[.,]00(?!\d)/, '');
  }

  function parseIntSafe(v) {
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : null;
  }

  function clampQuantity(v) {
    const n = parseIntSafe(v);
    return n && n > 0 ? n : 1;
  }

  function dispatchInputAndChange(el) {
    try {
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new Event("change", { bubbles: true }));
    } catch (_) {}
  }

  function findSectionRoot(container) {
    return (
      container.closest('[id^="shopify-section-"]') ||
      container.closest("[data-section-id]")?.closest('[id^="shopify-section-"]') ||
      container.closest("main") ||
      document
    );
  }

  function getSectionId(container, form) {
    const fromData = container.getAttribute("data-section-id");
    if (fromData) return fromData;

    const pf = container.closest("product-form");
    if (pf?.dataset?.sectionId) return pf.dataset.sectionId;

    const fromHidden = form?.querySelector('input[name="section-id"]')?.value;
    if (fromHidden) return fromHidden;

    return null;
  }

  function findAddToCartForm(container) {
    // Prefer direct form ID lookup (most reliable)
    const formId = container.dataset.formId;
    if (formId) {
      const f = document.getElementById(formId);
      if (f) return f;
    }

    const sectionRoot = findSectionRoot(container);

    // Prefer a form in a nearby product-form
    const nearestPF = container.closest("product-form");
    if (nearestPF) {
      const f = nearestPF.querySelector('form[action="/cart/add"]');
      if (f) return f;
    }

    // Otherwise, find within same section
    const forms = sectionRoot.querySelectorAll(
      'product-form form[action="/cart/add"], form[action="/cart/add"].shopify-product-form, form[action="/cart/add"]'
    );
    if (!forms.length) return null;
    if (forms.length === 1) return forms[0];

    // If multiple, match section id if possible
    const sidGuess = container.getAttribute("data-section-id") || null;
    if (sidGuess) {
      for (const f of forms) {
        const sid = f.querySelector('input[name="section-id"]')?.value;
        if (sid && sid === sidGuess) return f;

        const pf = f.closest("product-form");
        if (pf?.dataset?.sectionId === sidGuess) return f;
      }
    }

    return forms[0];
  }

  function ensureHiddenQuantityInput(form) {
    let el = form.querySelector('input[name="quantity"]');
    if (el) return el;

    el = document.createElement("input");
    el.type = "hidden";
    el.name = "quantity";
    el.value = "1";
    form.appendChild(el);
    return el;
  }

  function findUiQuantityInput(container, sectionId) {
    if (!sectionId) return null;
    const root = findSectionRoot(container);
    return root.querySelector(
      `quantity-input[data-section-id="${sectionId}"] input[name="quantity-js"]`
    );
  }

  function setActive(container, quantity) {
    container.querySelectorAll(BTN_SEL).forEach((btn) => {
      const btnQuantity = clampQuantity(btn.dataset.quantity);
      const isActive = btnQuantity === quantity;
      btn.parentElement.classList.toggle("active", isActive);
      btn.setAttribute("aria-pressed", String(isActive));
    });
  }

  function renderButtons(container, optionData) {
    const grid = container.querySelector(GRID_SEL);
    if (!grid) return;

    if (!optionData || !Array.isArray(optionData.options) || optionData.options.length === 0) {
      grid.innerHTML = "";
      return;
    }

    const options = [...optionData.options].sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
    const contentsPerUnit = Math.max(1, parseIntSafe(optionData.unitsPerItem) || 1);

    grid.innerHTML = options
      .map((opt) => {
        const quantity = Math.max(1, parseIntSafe(opt.quantity) || 1);
        const discountPercent = Math.min(100, Math.max(0, parseIntSafe(opt.discountPercent * 100) || 0));

        const totalContents = quantity * contentsPerUnit;
        const totalCents = optionData.price * quantity;
        const discountedTotalCents = Math.round((totalCents * (100 - discountPercent)) / 100);
        const perContentsCents = Math.round(discountedTotalCents / totalContents);

        const unitLabel = totalContents === 1 ? optionData.unitSingular : optionData.unitPlural;
        const suffix = (optionData.suffix || "").trim();
        const suffixText = suffix ? ` ${suffix}` : "";

        return `
          <div class="swatch-element">
            <button
              type="button"
              class="quantity-options--button"
              data-custom-quantity-option
              data-quantity="${quantity}"
              aria-pressed="false"
            >
              <span class="quantity-options--label">${totalContents} ${unitLabel}</span>
              <span class="quantity-options--divider">|</span>
              <span class="quantity-options--price">${formatMoney(perContentsCents)}${suffixText}</span>
            </button>
          </div>
        `;
      })
      .join("");
  }

  function initContainer(container) {
    if (container.hasAttribute(INIT_ATTR)) return;
    container.setAttribute(INIT_ATTR, "true");

    const dataEl = container.querySelector(DATA_SEL);
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    const form = findAddToCartForm(container);
    if (!form) return;

    const quantityHidden = ensureHiddenQuantityInput(form);
 
    const sectionId = getSectionId(container, form);
    const quantityUi = findUiQuantityInput(container, sectionId);

    function updateTotalPrice(quantity) {
      const wrapper = container.querySelector('[data-quantity-options-total-price]');
      if (!wrapper) return;

      const originalEl = wrapper.querySelector('[data-quantity-original-total]');
      const finalEl = wrapper.querySelector('[data-quantity-final-total]');

      const opt = data.options.find(o => Math.max(1, parseIntSafe(o.quantity) || 1) === quantity);
      if (!opt) return;

      const discountPercent = Math.min(100, Math.max(0, parseIntSafe(opt.discountPercent * 100) || 0));
      const totalCents = data.price * quantity;
      const discountedTotalCents = Math.round((totalCents * (100 - discountPercent)) / 100);

      if (originalEl) {
        const isDiscounted = discountPercent > 0;
        // Make it less busy when it's the original price is crossed out and has zeroes after the decimal.
        originalEl.textContent = isDiscounted ? formatMoneyRounded(totalCents) : formatMoney(totalCents);
        originalEl.classList.toggle('has-discount', isDiscounted);
      }
      if (finalEl) {
        finalEl.textContent = formatMoney(discountedTotalCents);
      }
    }

    let selectedQuantity = clampQuantity(quantityHidden.value || 1);

    function setQuantity(quantity) {
      const n = clampQuantity(quantity);
      selectedQuantity = n;

      // Actual cart quantity (submitted)
      quantityHidden.value = String(n);
      dispatchInputAndChange(quantityHidden);

      // Flow's UI quantity
      if (quantityUi) {
        quantityUi.value = String(n);
        dispatchInputAndChange(quantityUi);
      }

      setActive(container, n);
      updateTotalPrice(n);
    }

    // Sync active state if quantity changes elsewhere (plus/minus UI, etc.)
    function syncActive() {
      const currentQuantity = clampQuantity(quantityHidden.value || (quantityUi ? quantityUi.value : 1));
      setActive(container, currentQuantity);
    }

    quantityHidden.addEventListener("input", syncActive);
    quantityHidden.addEventListener("change", syncActive);
    if (quantityUi) {
      quantityUi.addEventListener("input", syncActive);
      quantityUi.addEventListener("change", syncActive);
    }

    // Re-stamp quantity after theme resets input[name="quantity"] to 1 on cart:added
    if (window.eventBus) {
      window.eventBus.on("cart:added", (event) => {
        if (event.sectionId && event.sectionId !== sectionId) return;
        setTimeout(() => {
          quantityHidden.value = String(selectedQuantity);
        }, 0);
      });
    }

    // Ensure quantity is always correct at submit time (belt-and-suspenders)
    form.addEventListener("submit", () => {
      const qInput = form.querySelector('input[name="quantity"]');
      if (qInput) qInput.value = String(clampQuantity(quantityHidden.value));
    }, true);

    // Render buttons from product-level option data
    renderButtons(container, data);

    container.querySelectorAll(BTN_SEL).forEach((btn) => {
      btn.addEventListener("click", () => setQuantity(btn.dataset.quantity));
    });

    const currentQuantity = clampQuantity(quantityHidden.value || (quantityUi ? quantityUi.value : 1));
    setActive(container, currentQuantity);
    updateTotalPrice(currentQuantity);
  }

  function boot(root = document) {
    root.querySelectorAll(CONTAINER_SEL).forEach(initContainer);
  }

  // Initial boot
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(), { once: true });
  } else {
    boot();
  }

  // Re-init after Flow swaps HTML (data-replace-content updates)
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(CONTAINER_SEL)) initContainer(node);
        node.querySelectorAll?.(CONTAINER_SEL).forEach(initContainer);
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
